import { Dispatch } from 'redux';
import statsig from 'statsig-js';
import {
    StatsigApplePayVariants,
    STATSIG_APPLE_PAY_EXPERIMENT_KEY,
    STATSIG_APPLE_PAY_PARAM,
    STATSIG_CLIENT_KEY,
    STATSIG_LOGIN_INTERSTITIAL_EXPERIMENT_KEY,
} from '../constants/statsig';
import { isStatsigFreeTrialEnabled } from '../selectors/statsig';
import { getLoginFromCheckoutPage, isUserLoggedIn } from '../selectors/user';
import { CheckoutState } from '../state';
import { Experiment } from '../state/statsig';
import {
    getExperimentValueToOverride,
    getStatsigOptions,
    getStatsigUser,
} from '../utils/statsig';

const ensureSDKIsInitialized = async (
    dispatch: Dispatch<CheckoutState>,
    fn: () => any,
) => {
    if (!(statsig as any).instance) {
        await dispatch(initializeExperimentSdk());
    }
    return fn();
};

export const initializeExperimentSdk = () => async (
    dispatch: Dispatch<CheckoutState>,
    getState: () => CheckoutState,
) => {
    if (isStatsigFreeTrialEnabled(getState())) {
        await statsig.initialize(
            STATSIG_CLIENT_KEY,
            getStatsigUser(getState()),
            getStatsigOptions(),
        );

        const applePayExperimentValue = await dispatch(
            getExperimentValue(
                STATSIG_APPLE_PAY_EXPERIMENT_KEY,
                STATSIG_APPLE_PAY_PARAM,
                StatsigApplePayVariants.Hidden,
            ),
        );

        dispatch(
            saveExperimentDataAction({
                name: STATSIG_APPLE_PAY_EXPERIMENT_KEY,
                value: applePayExperimentValue,
            }),
        );
    }
};

export const updateExperimentUser = () => async (
    _: Dispatch<CheckoutState>,
    getState: () => CheckoutState,
) => {
    if (isStatsigFreeTrialEnabled(getState())) {
        await statsig.updateUser(getStatsigUser(getState()));
    }
};

export const logExperimentEvent = (
    eventName: string,
    eventValue?: any,
    eventMetadata?: any,
) => (dispatch: Dispatch<CheckoutState>, getState: () => CheckoutState) => {
    if (isStatsigFreeTrialEnabled(getState())) {
        ensureSDKIsInitialized(dispatch, () =>
            statsig.logEvent(eventName, eventValue, eventMetadata),
        );
    }
};

export const getExperimentValue = (
    experimentName: string,
    paramName: string,
    defaultValue: any,
) => async (
    dispatch: Dispatch<CheckoutState>,
    getState: () => CheckoutState,
) => {
    const state = getState();

    if (isStatsigFreeTrialEnabled(state)) {
        const valueToOverride = getExperimentValueToOverride(
            state,
            experimentName,
        );

        if (valueToOverride) {
            return valueToOverride;
        }

        return await ensureSDKIsInitialized(dispatch, () =>
            statsig.getExperiment(experimentName).get(paramName, defaultValue),
        );
    }
    return null;
};

export const isLoginInterstitialEnabled = () => async (
    dispatch: Dispatch<CheckoutState>,
    getState: () => CheckoutState,
) => {
    if (isUserLoggedIn(getState())) {
        return false;
    }

    return await dispatch(
        getExperimentValue(
            STATSIG_LOGIN_INTERSTITIAL_EXPERIMENT_KEY,
            'has_interstitial_step',
            false,
        ),
    );
};

export const logLoginInterstitialEvent = (eventName: string) => (
    dispatch: Dispatch<CheckoutState>,
    getState: () => CheckoutState,
) => {
    dispatch(
        logExperimentEvent(eventName, null, {
            isInterstitialStep: !getLoginFromCheckoutPage(getState()),
        }),
    );
};

export enum StatsigActionType {
    saveExperimentData = 'saveExperimentData',
}

export interface SaveExperimentDataAction {
    type: StatsigActionType.saveExperimentData;
    payload: Experiment;
}
export const saveExperimentDataAction = (
    experiment: Experiment,
): SaveExperimentDataAction => ({
    type: StatsigActionType.saveExperimentData,
    payload: experiment,
});

export type StatsigAction = SaveExperimentDataAction;
